import { useParams } from 'react-router-dom'
import { useOfflineFirstQuery } from '../../../hooks/useOfflineFirstQuery'
import { apiClient, getErrorMessage } from '../../../api/client'

type ClaimLine = {
  id: string
  service_code: string
  units: string
  unit_rate: string
  line_total: string
  service_date: string
}

type ClaimDetail = {
  id: string
  status: string
  payer_name: string
  client_name: string
  total_amount: string
  service_date: string
  diagnosis_codes: string[]
  lines: ClaimLine[]
}

export default function ClaimDetailPage() {
  const { claimId } = useParams()

  const { data, isFetching, error, refetch } = useOfflineFirstQuery<ClaimDetail>({
    queryKey: ['claim', claimId],
    queryFn: async () => {
      const res = await apiClient.get(`/api/v1/billing/claims/${claimId}/`)
      return res.data as ClaimDetail
    },
    cacheKey: `claim:${claimId}`,
    enabled: Boolean(claimId),
  })

  const handleScrub = async () => {
    if (!claimId) return
    await apiClient.post(`/api/v1/billing/claims/${claimId}/scrub/`)
    await refetch()
  }

  const handleSubmit = async () => {
    if (!claimId) return
    await apiClient.post(`/api/v1/billing/claims/${claimId}/submit/`)
    await refetch()
  }

  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
        {getErrorMessage(error)}
      </div>
    )
  }

  if (!data) {
    return (
      <div className="rounded-md border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700">
        {isFetching ? 'Loading…' : 'Claim not found'}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">Claim</p>
          <h1 className="text-xl font-semibold text-gray-900">{data.id}</h1>
          <p className="text-sm text-gray-600">
            {data.client_name} • {data.payer_name}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleScrub}
            className="rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Scrub
          </button>
          <button
            onClick={handleSubmit}
            className="rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500"
          >
            Submit
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h2 className="text-sm font-semibold text-gray-900">Status</h2>
          <p className="mt-2 text-sm text-gray-700">{data.status}</p>
          <p className="mt-2 text-sm text-gray-700">Service date: {data.service_date}</p>
          <p className="mt-2 text-sm text-gray-700">Total: {data.total_amount}</p>
          <p className="mt-2 text-sm text-gray-700">
            Dx: {data.diagnosis_codes?.join(', ') || '—'}
          </p>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h2 className="text-sm font-semibold text-gray-900">Actions</h2>
          <ul className="mt-2 space-y-2 text-sm text-gray-700">
            <li>• Scrubber blocks submission on errors</li>
            <li>• Submission sets status to READY_TO_SUBMIT</li>
            <li>• Edits must be done in the underlying session/note</li>
          </ul>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Line</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Units</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {data.lines?.map((line) => (
              <tr key={line.id}>
                <td className="px-4 py-3 text-sm text-gray-700">{line.service_date}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{line.service_code}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{line.units}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{line.unit_rate}</td>
                <td className="px-4 py-3 text-sm text-gray-900">{line.line_total}</td>
              </tr>
            ))}
            {(!data.lines || data.lines.length === 0) && (
              <tr>
                <td colSpan={5} className="px-4 py-4 text-center text-sm text-gray-500">
                  No claim lines.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
